import {
    ExposedOldAPIFormState,
    getActiveFormsState
} from '../../../../_shared/GWTBridge/invoke/formTable';
import {priority, wcfclient} from '@priority-software/priority-proto-api/lib';
import {BatchRequest} from '../../../../services/proto/core/batchRequest';
import {batchKeys} from '../../../../services/proto/core/constants';
import {getAdditionalProps} from '../../../../services/proto/core/loginProps';
import {APIActionStatus, Form, formsStore} from '../../../../stores/Form';
import {createFiltersFromExposedKeys, trySetCurrentRowForMultiCompForms} from './utils';
import {addNewScreen, handleScreenMessage} from '../../../Form/actions/screen';
import {fetchColumns, noDefaultMessageHandling, startForm} from '../../../../services/form/form';
import {findFormByLevel} from '../../../Form/utils/findFormByLevel';
import {setFormSystemConstants} from '../../../Form/actions/form';
import {getRowKeysValues} from '../../../../_legacy/Form/services/formService';
import {getLang} from '../../../../Localization';

/**
 * Hebrew to English month mapping for date parsing
 */
const hebrewToEnglishMonths = {
    ינו: 'Jan',
    פבר: 'Feb',
    מרץ: 'Mar',
    אפר: 'Apr',
    מאי: 'May',
    יונ: 'Jun',
    יול: 'Jul',
    אוג: 'Aug',
    ספט: 'Sep',
    אוק: 'Oct',
    נוב: 'Nov',
    דצמ: 'Dec'
};

/**
 * Enhanced date parser for custom Date14 formats
 * Handles both English and Hebrew date formats
 * @param inputValue - The original date value from the key
 * @returns Formatted date string in appropriate locale format
 */
const parseCustomDate14 = (inputValue: string): string => {
    const lang = getLang();

    try {
        let parsableDate: string;

        if (lang === '1') {
            // Hebrew format: '20:14 29-אוק-25'
            // Convert Hebrew month to English
            let convertedDate = inputValue;
            Object.keys(hebrewToEnglishMonths).forEach(hebrewMonth => {
                if (convertedDate.includes(hebrewMonth)) {
                    convertedDate = convertedDate.replace(
                        hebrewMonth,
                        hebrewToEnglishMonths[hebrewMonth]
                    );
                }
            });

            parsableDate = convertedDate;
        } else {
            // English format: 'Oct-29-25 20:14'
            parsableDate = inputValue;
        }

        const date = new Date(parsableDate);

        const datePart = date.toLocaleDateString(lang === '3' ? 'en-US' : 'en-GB', {
            year: '2-digit',
            month: '2-digit',
            day: '2-digit'
        });
        const timePart = date.toLocaleTimeString(lang === '3' ? 'en-US' : 'en-GB', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: lang === '3'
        });
        const result = `${datePart} ${timePart}`;
        return result;
    } catch (error) {
        console.error(`Error parsing date "${inputValue}":`, error);
        return inputValue; // Return original if parsing fails
    }
};

export const runBridgeSubformBatch = async (
    session: string,
    childForm: ExposedOldAPIFormState,
    keys: priority.netitems.form.IField[]
): Promise<[priority.netitems.form.IStartRs, priority.netitems.form.IColumnsRs]> => {
    const batchRequest = new BatchRequest(session);

    batchRequest.addTask({
        key: batchKeys.Subform,
        request: new priority.netitems.form.SubformRq({
            Fname: childForm.formName,
            Keys: keys,
            Session: session,
            Properties: getAdditionalProps()
        })
    });

    batchRequest.addTask({
        key: batchKeys.Columns,
        request: new priority.netitems.form.ColumnsRq({
            Session: session,
            Properties: getAdditionalProps()
        })
    });

    const res = await batchRequest.getData();

    return [res[0]?.Subform, res[1]?.Columns];
};

export const closeBridgeForms = async (session: string) => {
    const formState = getActiveFormsState();
    const batchRequest = new BatchRequest(session);

    formState.forEach(() => {
        batchRequest.addTask({
            key: batchKeys.End,
            request: new priority.netitems.form.EndRq({
                Session: session,
                Properties: getAdditionalProps()
            })
        });
    });

    const res = await batchRequest.getData();

    //Clear all form info
    formsStore.deleteForm(session);
    formsStore.setCurrentSession('');

    return [res[0]?.End, res[1]?.End];
};

export const handleSubformForAutomations = async (
    masterForm: Form,
    childFormData: ExposedOldAPIFormState,
    fetchRecords: boolean
) => {
    const parentCurrentRecord = masterForm.records[masterForm.currentRow || 0];
    const keys = parentCurrentRecord.keys;

    const subformBatch = await runBridgeSubformBatch(masterForm.session, childFormData, keys);

    const [startSubFormRes, columnsRs] = subformBatch;
    const currentLevel = childFormData.level;
    const subform = new Form(childFormData.formName, startSubFormRes, columnsRs, currentLevel, {});
    subform.setParentForm(masterForm);

    const Filters = createFiltersFromExposedKeys(childFormData.keys, subform);

    if (fetchRecords && Filters) {
        const subformFetchRes = await wcfclient.recordsFetch(
            new priority.netitems.form.FetchRq({
                Session: subform.session,
                Properties: getAdditionalProps(),
                Filters,
                Sort: [],
                Top: 1,
                Attachments: priority.netitems.form.AttachmentOptions.Attach_NoData
            })
        );
        return await handleScreenMessage(subformFetchRes, {}, async () => {
            subform.setRecords(subformFetchRes?.Records || []);
        });
    }
    return APIActionStatus.passed;
};

export const runBridgeForm = async (fetchCurrentForm: boolean = false) => {
    const formKeysTree = await getRowKeysValues();
    const reversedFormState: ExposedOldAPIFormState[] = formKeysTree['Form']?.[0]?.['Keys']?.map(
        (item, index) => {
            return {
                formName: item['fname'],
                keys: item['KeysGroup'][0]['Key'].map(({name, value}) => {
                    return {columnName: name, value};
                }),
                level: formKeysTree['Form'][0]['Keys'].length - 1 - index
            };
        }
    );

    const formState = reversedFormState?.reverse() || [];

    const masterFormData = formState[0];

    if (formState.length > 0) {
        await setFormSystemConstants();
        const startRs: priority.netitems.form.StartRs = await startForm(
            masterFormData.formName,
            noDefaultMessageHandling
        );
        const isFormOk = await handleScreenMessage(startRs);
        if (isFormOk) {
            const columnsRs = await fetchColumns(startRs.Session);
            const isColumnOk = await handleScreenMessage(columnsRs);

            if (isColumnOk) {
                const masterForm: Form = new Form(
                    masterFormData.formName,
                    startRs,
                    columnsRs,
                    0,
                    {}
                );

                masterFormData.keys.forEach(key => {
                    const column = columnsRs.Columns.find(col => col.Name === key.columnName);
                    if (column && column.Type === priority.netitems.form.ColumnType.Date14) {
                        key.value = parseCustomDate14(key.value);
                    }
                });

                const subforms = formState.slice(1, formState.length);
                const Filters = createFiltersFromExposedKeys(masterFormData.keys, masterForm);

                if ((subforms.length > 0 || fetchCurrentForm) && Filters) {
                    const fetchRes = await wcfclient.recordsFetch(
                        new priority.netitems.form.FetchRq({
                            Session: masterForm.session,
                            Properties: getAdditionalProps(),
                            Filters,
                            Sort: [],
                            Top: 1,
                            Attachments: priority.netitems.form.AttachmentOptions.Attach_NoData
                        })
                    );

                    if (fetchRes.Records.length === 0) {
                        return null;
                    }

                    await handleScreenMessage(fetchRes, {}, async () => {
                        masterForm.setRecords(fetchRes?.Records || []);
                        trySetCurrentRowForMultiCompForms(masterForm);
                        for (const childForm of subforms) {
                            const parentForm = findFormByLevel(masterForm, childForm.level - 1);
                            const fetchRecords =
                                childForm.level < subforms.length ||
                                (fetchCurrentForm && childForm.level === subforms.length);
                            const res = await handleSubformForAutomations(
                                parentForm,
                                childForm,
                                fetchRecords
                            );

                            if (res === APIActionStatus.blocked) {
                                break;
                            }
                        }
                    });
                }

                addNewScreen(masterForm, null, true);

                formsStore.currentScreen.setActiveLevel(subforms.length);

                return masterForm;
            }
        }
    }
    return null;
};
